import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", gstin: "", pan: "", entity_type: "Proprietorship", notes: "" };

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/clients").then((r) => setClients(r.data.clients));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <Header title="Clients" subtitle="Your book of business" onNew={openNew} newLabel="Add Client" testId="clients" />

      <div className="mt-6 border border-[#E8EAE8] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E8EAE8] text-[11px] uppercase tracking-widest text-[#5C6B64] font-semibold">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2 font-mono-data">GSTIN</div>
          <div className="col-span-2 font-mono-data">PAN</div>
          <div className="col-span-1">Entity</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {clients.length === 0 && <div className="p-6 text-sm text-[#5C6B64]">No clients yet.</div>}
        {clients.map((c) => (
          <div key={c.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b last:border-b-0 border-[#E8EAE8] items-center" data-testid={`client-row-${c.id}`}>
            <div className="col-span-3 text-[#0F2E22] font-medium">{c.name}</div>
            <div className="col-span-3 text-sm text-[#3B463F]">{c.email || "—"}</div>
            <div className="col-span-2 font-mono-data text-xs text-[#3B463F]">{c.gstin || "—"}</div>
            <div className="col-span-2 font-mono-data text-xs text-[#3B463F]">{c.pan || "—"}</div>
            <div className="col-span-1 text-xs text-[#3B463F]">{c.entity_type}</div>
            <div className="col-span-1 flex justify-end gap-2">
              <IconBtn onClick={() => openEdit(c)} testId={`edit-client-${c.id}`}><PencilSimple size={14} /></IconBtn>
              <IconBtn onClick={() => remove(c)} testId={`delete-client-${c.id}`}><Trash size={14} /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Client" : "Add Client"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <F label="Name" value={form.name} onChange={set("name")} required testId="form-name" />
            <div className="grid grid-cols-2 gap-3">
              <F label="Email" type="email" value={form.email} onChange={set("email")} testId="form-email" />
              <F label="Phone" value={form.phone} onChange={set("phone")} testId="form-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="GSTIN" value={form.gstin} onChange={set("gstin")} mono testId="form-gstin" />
              <F label="PAN" value={form.pan} onChange={set("pan")} mono testId="form-pan" />
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Entity type</span>
              <select value={form.entity_type} onChange={set("entity_type")} data-testid="form-entity" className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                {["Proprietorship", "Partnership", "LLP", "Private Limited", "OPC", "HUF", "Trust", "Individual"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </label>
            <F label="Notes" value={form.notes} onChange={set("notes")} testarea testId="form-notes" />
            <button type="submit" className="w-full py-3 bg-[#0F2E22] text-white text-sm tracking-widest uppercase hover:opacity-90" data-testid="save-client-btn">Save</button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export const Header = ({ title, subtitle, onNew, newLabel, testId }) => (
  <div className="flex items-end justify-between">
    <div>
      <p className="text-xs tracking-[0.3em] uppercase text-[#5C6B64]">{subtitle}</p>
      <h1 className="font-serif-display text-4xl mt-2 text-[#0F2E22]">{title}</h1>
    </div>
    {onNew && (
      <button
        onClick={onNew}
        data-testid={`new-${testId}-btn`}
        className="brand-gold-bg px-5 py-2.5 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 hover:opacity-90"
      >
        <Plus size={14} weight="bold" /> {newLabel}
      </button>
    )}
  </div>
);

export const F = ({ label, testarea, mono, testId, ...rest }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-[#5C6B64]">{label}</span>
    {testarea ? (
      <textarea {...rest} data-testid={testId} rows={2} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white outline-none focus:border-[#0F2E22]" />
    ) : (
      <input {...rest} data-testid={testId} className={`mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white outline-none focus:border-[#0F2E22] ${mono ? "font-mono-data" : ""}`} />
    )}
  </label>
);

export const IconBtn = ({ children, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="p-1.5 border border-[#E8EAE8] hover:bg-[#F3F1EC] text-[#3B463F]">{children}</button>
);

export const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white w-full max-w-lg border border-[#E8EAE8]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EAE8]">
        <h3 className="font-serif-display text-2xl text-[#0F2E22]">{title}</h3>
        <button onClick={onClose} data-testid="modal-close-btn" className="text-[#5C6B64] hover:text-[#0F2E22]"><X size={18} /></button>
      </div>
      <div className="p-5 max-h-[80vh] overflow-y-auto">{children}</div>
    </div>
  </div>
);

export default Clients;
